/**
 * Klaviyo Signup Enhancements (REQ-011 FR-009/FR-010)
 *
 * Unified sticky tab + page-type popup timing.
 *
 * Sticky Tab:
 * - Left-side vertical tab replaces previous mobile banner + desktop teaser
 * - Auto-hides on mobile product pages when near ATC/bundle section
 * - 4-day dismissal memory, header CTA fallback after manual close
 *
 * Page-Type Popup Timing (FR-009):
 * - Product pages: exit intent only
 * - Homepage: 8s delay OR 50% scroll, plus exit intent
 * - Collection pages: 12s delay OR 60% scroll, plus exit intent
 * - All pages: custom trigger via tab/header CTA click
 */
(function () {
  'use strict';

  // Configuration from theme settings (injected by Liquid)
  var config = {
    formId: window.klaviyoFormId || 'YOUR_FORM_ID',
    dismissalDays: window.klaviyoDismissalDays || 4,
    fallbackUrl: '/pages/newsletter-signup'
  };

  // Page type detection
  var bodyEl = document.body;
  var isProductPage = bodyEl.classList.contains('template-product');
  var isHomePage = bodyEl.classList.contains('template-index') || bodyEl.classList.contains('template-frontpage');
  var isCollectionPage = bodyEl.classList.contains('template-collection');

  // State
  var isFormOpen = false;
  var formOpenTimeout = null;
  var popupTriggered = false; // Prevent duplicate popup triggers
  var exitIntentBound = false;

  // Storage keys
  var STORAGE_KEYS = ['klaviyoCTADismissal', 'bannerHiddenSession', 'popupAutoFiredSession'];
  var storageInitialized = false;

  // ========== STORAGE HELPERS ==========

  function isLocalStorageAvailable() {
    try {
      var test = '__localStorage_test__';
      window.localStorage.setItem(test, 'test');
      window.localStorage.removeItem(test);
      return true;
    } catch (_e) {
      return false;
    }
  }

  function syncStorageOnLoad() {
    if (storageInitialized) return;
    if (isLocalStorageAvailable()) {
      STORAGE_KEYS.forEach(function (key) {
        try {
          var value = window.localStorage.getItem(key);
          if (value !== null) {
            sessionStorage.setItem(key, value);
          }
        } catch (_e) {
          // Silent fail
        }
      });
    }
    storageInitialized = true;
  }

  function getStorageItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_e) {
      return null;
    }
  }

  function setStorageItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
      if (isLocalStorageAvailable()) {
        try {
          window.localStorage.setItem(key, value);
        } catch (_e) {
          // Silent fail
        }
      }
    } catch (_e) {
      // Silent fail
    }
  }

  function removeStorageItem(key) {
    try {
      sessionStorage.removeItem(key);
      if (isLocalStorageAvailable()) {
        try {
          window.localStorage.removeItem(key);
        } catch (_e) {
          // Silent fail
        }
      }
    } catch (_e) {
      // Silent fail
    }
  }

  // ========== DISMISSAL LOGIC ==========

  function setCTADismissal(reason) {
    var dismissalData = {
      dismissed: true,
      timestamp: new Date().getTime(),
      reason: reason || 'manual_close'
    };
    setStorageItem('klaviyoCTADismissal', JSON.stringify(dismissalData));
  }

  function hasCTABeenDismissed() {
    var dismissalData = getStorageItem('klaviyoCTADismissal');
    if (!dismissalData) return false;
    try {
      var data = JSON.parse(dismissalData);
      return data.dismissed === true;
    } catch (_e) {
      return false;
    }
  }

  function isCTADismissalExpired() {
    var dismissalData = getStorageItem('klaviyoCTADismissal');
    if (!dismissalData) return true;
    try {
      var data = JSON.parse(dismissalData);
      if (data.reason === 'signup') return false;
      var daysSinceDismissal = (new Date().getTime() - data.timestamp) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal >= config.dismissalDays) {
        removeStorageItem('bannerHiddenSession');
        return true;
      }
      return false;
    } catch (_e) {
      return true;
    }
  }

  function shouldShowCTA() {
    if (getStorageItem('bannerHiddenSession')) return false;
    if (hasCTABeenDismissed() && !isCTADismissalExpired()) return false;
    return true;
  }

  // ========== TAB SHOW/HIDE ==========

  function showTab() {
    var tab = document.getElementById('klaviyo-sticky-tab');
    if (!tab) return;
    tab.classList.remove('hidden');
    tab.style.display = 'block';
    hideHeaderCTA();
  }

  function hideTab() {
    var tab = document.getElementById('klaviyo-sticky-tab');
    if (!tab) return;
    tab.classList.add('hidden');
  }

  function showHeaderCTA() {
    var headerCta = document.getElementById('klaviyo-header-cta');
    var stickyHeaderCta = document.getElementById('klaviyo-header-cta-sticky');

    [headerCta, stickyHeaderCta].forEach(function (cta) {
      if (cta) {
        cta.style.visibility = 'hidden';
        cta.classList.add('is-visible');
        window.dispatchEvent(new window.Event('resize'));
        setTimeout(function () {
          cta.style.visibility = 'visible';
        }, 1);
      }
    });
  }

  function hideHeaderCTA() {
    var headerCta = document.getElementById('klaviyo-header-cta');
    var stickyHeaderCta = document.getElementById('klaviyo-header-cta-sticky');

    [headerCta, stickyHeaderCta].forEach(function (cta) {
      if (cta) {
        cta.classList.remove('is-visible');
      }
    });
  }

  function hideAllCTAs() {
    hideTab();
    hideHeaderCTA();
  }

  function handleTabDismissal() {
    hideTab();
    showHeaderCTA();
    setCTADismissal('manual_close');
    setStorageItem('bannerHiddenSession', 'true');
  }

  function handleSignup() {
    hideAllCTAs();
    setCTADismissal('signup');
  }

  // ========== MOBILE PRODUCT PAGE: AUTO-HIDE NEAR ATC ==========

  function setupMobileAutoHide() {
    if (!isProductPage || window.innerWidth >= 768) return;

    var purchaseZone = document.querySelector('.product-purchase-zone');
    var tab = document.getElementById('klaviyo-sticky-tab');

    if (!tab || !purchaseZone) return;

    var tabRect = tab.getBoundingClientRect();
    var vh = window.innerHeight;

    // Band from tab top to tab bottom.
    // isIntersecting: true  → zone overlaps the tab → retract
    // isIntersecting: false → zone has fully cleared the tab → expand
    var marginTop = '-' + Math.round(tabRect.top) + 'px';
    var marginBottom = '-' + Math.round(vh - tabRect.bottom) + 'px';

    // Track zone state for re-retract after tap-to-expand
    var zoneIntersecting = false;

    new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          zoneIntersecting = entry.isIntersecting;
          if (zoneIntersecting) {
            tab.classList.add('retracted');
          } else {
            tab.classList.remove('retracted');
          }
        });
      },
      { rootMargin: marginTop + ' 0px ' + marginBottom + ' 0px', threshold: 0 }
    ).observe(purchaseZone);

    // Re-retract 2s after user taps retracted sliver to expand, if still in zone
    tab.addEventListener('transitionend', function () {
      if (zoneIntersecting && !tab.classList.contains('retracted')) {
        setTimeout(function () {
          if (zoneIntersecting) {
            tab.classList.add('retracted');
          }
        }, 2000);
      }
    });
  }

  // ========== PAGE-TYPE POPUP TIMING (FR-009) ==========

  function openKlaviyoForm() {
    if (popupTriggered || isFormOpen) return;
    if (getStorageItem('popupAutoFiredSession')) return; // Already fired this session
    popupTriggered = true;
    setStorageItem('popupAutoFiredSession', 'true');

    if (window._klOnsite && window._klOnsite.push) {
      window._klOnsite.push(['openForm', config.formId]);
    }
  }

  // Exit intent: mouse leaves viewport top (desktop only)
  function setupExitIntent() {
    if (exitIntentBound) return;
    exitIntentBound = true;

    document.addEventListener(
      'mouseout',
      function (e) {
        if (e.clientY <= 0 && !popupTriggered && !isFormOpen && shouldShowCTA()) {
          openKlaviyoForm();
        }
      },
      { passive: true }
    );
  }

  // Time delay trigger
  function setupTimeDelay(delayMs) {
    setTimeout(function () {
      if (!popupTriggered && !isFormOpen && shouldShowCTA()) {
        openKlaviyoForm();
      }
    }, delayMs);
  }

  // Scroll percentage trigger
  function setupScrollTrigger(percentage) {
    var triggered = false;

    window.addEventListener(
      'scroll',
      function () {
        if (triggered || popupTriggered || isFormOpen) return;

        var scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) return;

        var scrollPercent = (window.scrollY / scrollHeight) * 100;
        if (scrollPercent >= percentage) {
          triggered = true;
          if (shouldShowCTA()) {
            openKlaviyoForm();
          }
        }
      },
      { passive: true }
    );
  }

  function setupPageTypeTiming() {
    // Only set up auto-triggers if user hasn't dismissed
    if (!shouldShowCTA()) return;

    if (isProductPage) {
      // Product pages: exit intent only
      setupExitIntent();
    } else if (isHomePage) {
      // Homepage: 8s delay OR 50% scroll, plus exit intent
      setupTimeDelay(8000);
      setupScrollTrigger(50);
      setupExitIntent();
    } else if (isCollectionPage) {
      // Collection pages: 12s delay OR 60% scroll, plus exit intent
      setupTimeDelay(12000);
      setupScrollTrigger(60);
      setupExitIntent();
    }
    // Other pages: no auto-trigger, manual click only
  }

  // ========== FORM TRIGGER CLICK HANDLER ==========

  function handleFormTrigger(e, trigger) {
    e.preventDefault();

    if (isFormOpen) return;

    var formId = trigger.getAttribute('data-klaviyo-form-id');
    if (!formId) return;

    isFormOpen = true;

    if (window._klOnsite && window._klOnsite.push) {
      window._klOnsite.push(['openForm', formId]);

      clearTimeout(formOpenTimeout);
      formOpenTimeout = setTimeout(function () {
        isFormOpen = false;
      }, 3000);
    } else {
      window.location.href = config.fallbackUrl;
      isFormOpen = false;
    }
  }

  // ========== KLAVIYO EVENT LISTENERS ==========

  function setupKlaviyoListeners() {
    window.addEventListener('klaviyoForms', function (e) {
      if (e.detail.type === 'open') {
        clearTimeout(formOpenTimeout);
        hideTab();
      }

      if (e.detail.type === 'close') {
        isFormOpen = false;
        clearTimeout(formOpenTimeout);
        // Restore CTAs based on current state
        showCTAsBasedOnState();
      }

      if (e.detail.type === 'submit') {
        handleSignup();
      }
    });
  }

  function showCTAsBasedOnState() {
    var dismissalData = getStorageItem('klaviyoCTADismissal');

    if (dismissalData) {
      try {
        var data = JSON.parse(dismissalData);

        if (data.reason === 'signup') return;

        if (data.reason === 'manual_close' && !isCTADismissalExpired()) {
          showHeaderCTA();
        } else {
          showTab();
        }
      } catch (_e) {
        showTab();
      }
    } else {
      showTab();
    }
  }

  // ========== INITIALIZE ==========

  function init() {
    syncStorageOnLoad();
    setupKlaviyoListeners();

    // Click handler for all triggers (tab, header CTA)
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.klaviyo_form_trigger');
      if (trigger) {
        handleFormTrigger(e, trigger);
      }
    });

    // Tab close button
    var closeBtn = document.getElementById('tab-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleTabDismissal();
      });
    }

    // Tap retracted sliver to restore full tab
    var tab = document.getElementById('klaviyo-sticky-tab');
    if (tab) {
      tab.addEventListener('click', function (e) {
        if (tab.classList.contains('retracted')) {
          e.preventDefault();
          e.stopPropagation();
          tab.classList.remove('retracted');
        }
      });
    }

    // Show CTAs based on initial state
    showCTAsBasedOnState();

    // Setup mobile auto-hide near ATC
    setupMobileAutoHide();

    // Setup page-type popup timing (fires once per session)
    setupPageTypeTiming();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
